import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSearch } from '@/contexts/SearchContext';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Clock, MapPin, Zap, UtensilsCrossed, ShoppingBasket, Pill, Wine, Sparkles, ArrowRight, Star } from 'lucide-react';
const heroImage = '/images/lumza-hero.webp';
import HeroParticles from '@/components/HeroParticles';
import { z } from 'zod';

const searchSchema = z.string().trim().max(100);

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.15 }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } }
};

const quickPicks = [
  { label: 'Late-night eats', query: 'food', icon: UtensilsCrossed },
  { label: 'Groceries', query: 'groceries', icon: ShoppingBasket },
  { label: 'Medication', query: 'pharmacy', icon: Pill },
  { label: 'Beverages', query: 'drinks', icon: Wine },
  { label: 'Essentials', query: 'essentials', icon: Sparkles },
];

import { getHeroVariant, getTimeBucket, trackHeroEvent, type HeroVariant, type TimeBucket } from '@/lib/heroAnalytics';
import { getHeroCopy, getRotatingPrompts } from '@/lib/heroCopy';

const HeroSection = () => {
  const { t } = useLanguage();
  const { setSearchQuery: setGlobalSearchQuery } = useSearch();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [promptIndex, setPromptIndex] = useState(0);
  const [variant] = useState<HeroVariant>(() => getHeroVariant());
  const [timeBucket, setTimeBucket] = useState<TimeBucket>(() => getTimeBucket());
  const copy = getHeroCopy(variant);
  const rotatingPrompts = getRotatingPrompts(variant, timeBucket);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const contentX = useTransform(mouseX, [-1, 1], [-8, 8]);
  const contentY = useTransform(mouseY, [-1, 1], [-6, 6]);
  const rectRef = useRef<DOMRect | null>(null);
  const rafPendingRef = useRef(false);
  const [interactive, setInteractive] = useState(true);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
    setInteractive(!reduceMotion && !coarsePointer);
    const id = setInterval(() => {
      setPromptIndex((i) => (i + 1) % rotatingPrompts.length);
    }, 2400);
    const bucketId = setInterval(() => setTimeBucket(getTimeBucket()), 60_000);
    return () => { clearInterval(id); clearInterval(bucketId); };
  }, [rotatingPrompts.length]);

  // Track impression once per session/variant
  useEffect(() => {
    trackHeroEvent('variant_assigned', { variant, time_bucket: timeBucket });
    trackHeroEvent('headline_view', { variant, time_bucket: timeBucket });
  }, [variant]);

  // Track each new prompt impression (debounced via index change)
  useEffect(() => {
    trackHeroEvent('prompt_view', {
      variant,
      prompt_index: promptIndex,
      prompt_label: rotatingPrompts[promptIndex],
      time_bucket: timeBucket,
    });
  }, [promptIndex, variant, timeBucket]);

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    rectRef.current = e.currentTarget.getBoundingClientRect();
  }, []);

  const handleMouseLeave = useCallback(() => {
    rectRef.current = null;
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!interactive || rafPendingRef.current) return;
      const rect = rectRef.current;
      if (!rect || !rect.width || !rect.height) return;
      const clientX = e.clientX;
      const clientY = e.clientY;
      rafPendingRef.current = true;
      requestAnimationFrame(() => {
        rafPendingRef.current = false;
        const x = (clientX - rect.left) / rect.width * 2 - 1;
        const y = (clientY - rect.top) / rect.height * 2 - 1;
        mouseX.set(x);
        mouseY.set(y);
      });
    },
    [mouseX, mouseY, interactive]
  );

  const submitSearch = (term: string, source: 'search' | 'quick_pick' = 'search') => {
    try {
      const validated = searchSchema.parse(term).trim();
      if (source === 'quick_pick') {
        trackHeroEvent('quick_pick_click', { variant, time_bucket: timeBucket, prompt_label: validated });
      } else {
        trackHeroEvent('search_submit', { variant, time_bucket: timeBucket, prompt_label: validated });
      }
      if (!validated) {
        scrollToStores();
        return;
      }
      setGlobalSearchQuery(validated);
      navigate(`/search?q=${encodeURIComponent(validated)}`);
    } catch {
      console.error('Invalid search input');
    }
  };

  const handlePromptClick = () => {
    const label = rotatingPrompts[promptIndex];
    trackHeroEvent('prompt_click', {
      variant,
      prompt_index: promptIndex,
      prompt_label: label,
      time_bucket: timeBucket,
    });
    submitSearch(label.replace(/[\p{Emoji}\u2014—\-–]+/gu, '').trim() || label);
  };

  const scrollToStores = () => {
    const el = document.querySelector('#stores-section');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <section
      className="relative min-h-[80vh] sm:min-h-[85vh] flex items-center justify-center overflow-hidden pt-20 sm:pt-28 md:pt-32 lg:pt-36 pb-10 sm:pb-16"
      style={{ contain: 'layout paint' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}>
      
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroImage}
          alt="LUMZA After-Hours Delivery"
          className="w-full h-full object-cover object-center"
          loading="eager"
          // @ts-ignore
          fetchpriority="high" />
        
        <div className="absolute inset-0 bg-gradient-to-r from-midnight/95 via-midnight/70 to-midnight/40 sm:from-midnight/90 sm:via-midnight/60 sm:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/50 to-transparent" />
      </div>

      {/* Animated Particles Layer */}
      <HeroParticles />

      {/* Content */}
      <motion.div
        className="relative z-10 container mx-auto px-4 sm:px-6 text-center max-w-5xl"
        style={{ x: contentX, y: contentY, willChange: 'transform' }}
        variants={stagger}
        initial="hidden"
        animate="visible">
        
        <div className="space-y-6 sm:space-y-8">
          {/* Live Status Pill */}
          <motion.div variants={fadeUp} className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-primary/10 border border-primary/30 backdrop-blur-md">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              <span className="text-[10px] sm:text-xs font-semibold text-primary tracking-wide uppercase">
                Open now · Delivering tonight
              </span>
            </div>
          </motion.div>

          {/* Main Heading */}
          <div className="space-y-2 sm:space-y-3 md:space-y-4">
            <motion.h1 className="font-bold leading-tight" variants={fadeUp}>
              <span className="hero-shimmer-text sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-9xl block drop-shadow-lg tracking-tight font-bold text-center text-7xl">
                LUMZA
              </span>
              <span className="text-moonlight text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl font-semibold block mt-1 sm:mt-2 drop-shadow-md">
                {copy.subtitle}
              </span>
            </motion.h1>

            {/* Rotating prompt */}
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-x-2 text-sm sm:text-base md:text-lg text-muted-foreground px-2">
              <span>{copy.prefix}</span>
              <span className="inline-block min-w-[140px] sm:min-w-[180px] text-left">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={promptIndex}
                    initial={{ y: 12, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -12, opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="text-neon font-semibold pointer-events-none select-none inline-block">
                    {rotatingPrompts[promptIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </motion.div>
          </div>

          {/* Search Bar */}
          <motion.form
            variants={fadeUp}
            onSubmit={(e) => { e.preventDefault(); submitSearch(searchInput, 'search'); }}
            className="max-w-xl mx-auto px-2"
          >
            <div className="group relative flex items-center gap-2 p-1.5 sm:p-2 rounded-2xl bg-dark-elevated/80 backdrop-blur-xl border border-border/60 hover:border-primary/50 focus-within:border-primary transition-all shadow-glow">
              <Search className="ml-2 sm:ml-3 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground shrink-0" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="What do you need tonight?"
                className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 h-9 sm:h-10 text-sm sm:text-base px-1 placeholder:text-muted-foreground/70"
                maxLength={100}
              />
              <Button
                type="submit"
                className="btn-neon h-9 sm:h-10 px-3 sm:px-5 rounded-xl text-xs sm:text-sm font-semibold gap-1 shrink-0"
              >
                Search
                <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
            </div>
          </motion.form>

          {/* Quick Pick Chips */}
          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-2 sm:gap-2.5 px-2 max-w-2xl mx-auto">
            {quickPicks.map((pick, i) => {
              const Icon = pick.icon;
              return (
                <motion.button
                  key={pick.query}
                  whileHover={{ scale: 1.06, y: -2 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={() => submitSearch(pick.query, 'quick_pick')}
                  className="group inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-dark-elevated/70 backdrop-blur-md border border-border/60 hover:border-primary hover:bg-primary/10 text-xs sm:text-sm font-medium text-foreground transition-all"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary group-hover:scale-110 transition-transform" />
                  <span>{pick.label}</span>
                </motion.button>
              );
            })}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            variants={fadeUp}
            className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center items-stretch sm:items-center px-2 max-w-md mx-auto">
            <Button
              onClick={() => {
                trackHeroEvent('cta_primary_click', { variant, time_bucket: timeBucket });
                scrollToStores();
              }}
              className="btn-neon w-full sm:w-auto px-6 md:px-8 py-2.5 sm:py-3 text-sm sm:text-base h-11 sm:h-12 font-semibold touch-target gap-2">
              <Zap className="h-4 w-4" />
              {t('hero.cta.primary')}
            </Button>
            <Button
              onClick={() => {
                trackHeroEvent('cta_secondary_click', { variant, time_bucket: timeBucket });
                navigate('/browse');
              }}
              variant="outline"
              className="btn-ghost-neon w-full sm:w-auto px-6 md:px-8 py-2.5 sm:py-3 text-sm sm:text-base h-11 sm:h-12 font-semibold touch-target">
              {t('hero.cta.secondary')}
            </Button>
          </motion.div>

          {/* Trust Stats Strip */}
          <motion.div
            variants={fadeUp}
            className="flex flex-wrap justify-center items-center gap-x-6 sm:gap-x-10 gap-y-3 pt-4 sm:pt-6 text-xs sm:text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4 text-primary" />
              <span><span className="text-foreground font-semibold">~30 min</span> avg delivery</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-border/60" />
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary" />
              <span><span className="text-foreground font-semibold">Joburg</span> wide</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-border/60" />
            <div className="flex items-center gap-2 text-muted-foreground">
              <Star className="h-4 w-4 text-primary fill-primary" />
              <span><span className="text-foreground font-semibold">4.9</span> rated by night owls</span>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Floating Elements */}
      <div className="hidden sm:block absolute top-16 md:top-20 left-6 md:left-10 w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-full blur-xl animate-pulse" />
      <div className="hidden sm:block absolute bottom-16 md:bottom-20 right-6 md:right-10 w-24 h-24 md:w-32 md:h-32 bg-primary/5 rounded-full blur-2xl float-animation" />

    </section>);

};

export default HeroSection;
